import { Component } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { MbsSize, ModalSettings } from 'mbs-ui-kit';
import { ComputerBackupFacade } from 'mbs-ui-shared@facades/computer.backup.facade';
import { ComputersFacade } from 'mbs-ui-shared@facades/computers.facade';
import Computer from 'mbs-ui-shared@models/Computer';
import { PlanFormatTypes } from 'mbs-ui-shared@models/PlanTypes.enum';
import { PlanInfo } from 'mbs-ui-shared@models/backup/plan-info-model';
import { RecoveryErrorSolutionName } from 'mbs-ui-shared@models/recovery-error-solution-name.enum';
import { OmitRSEData } from 'mbs-ui-shared@models/recovery-solution-execution-data.interface';
import { RecoverySolutionExecutor, RecoveryStatusService } from 'mbs-ui-shared@modules/recovery-status/services/recovery-status.service';
import { getWizardComponentType } from 'mbs-ui-shared@utils/getWizardComponentType';
import { PlanModalWrapper } from 'mbs-ui-shared@wrappers/plan.wrapper';
import { Observable, ReplaySubject, combineLatest, first, of, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';

@UntilDestroy()
@Component({
  selector: 'mbs-open-plan-on-selected-step-solution',
  template: '<span></span>'
})
export class OpenPlanOnSelectedStepSolutionComponent implements RecoverySolutionExecutor {
  private readonly modalSettings: ModalSettings = { size: MbsSize.lg };
  private readonly validSolutions = [
    RecoveryErrorSolutionName.OpenEditWizard,
    RecoveryErrorSolutionName.OpenScheduleWindow,
    RecoveryErrorSolutionName.OpenAdvancedFilter,
    RecoveryErrorSolutionName.OpenRetentionPolicy,
    RecoveryErrorSolutionName.SelectAvailableSource
  ];
  private readonly firstStepsFromSolution = {
    [RecoveryErrorSolutionName.OpenScheduleWindow]: this.i18nPipe.transform('wizards:schedule_title', { format: 'title' }),
    [RecoveryErrorSolutionName.OpenAdvancedFilter]: this.i18nPipe.transform('wizards:advanced_filter_title', { format: 'title' }),
    [RecoveryErrorSolutionName.OpenRetentionPolicy]: this.i18nPipe.transform('wizards:retention_policy_title', { format: 'title' }),
    [RecoveryErrorSolutionName.SelectAvailableSource]: this.i18nPipe.transform('wizards:what_backup_step_title', { format: 'title' })
  };

  constructor(
    public i18nPipe: I18NextPipe,
    public computersFacade: ComputersFacade,
    public facade: ComputerBackupFacade,
    public recoveryStatusService: RecoveryStatusService,
    private planWrapper: PlanModalWrapper,
    private wizardService: RemoteManagementWizardsService
  ) {
    new RecoveryStatusService.RecoverySolutionSubscriber(this);
  }

  canExecuteSolution(data: OmitRSEData): boolean {
    return this.validSolutions.includes(data?.solution?.action_type);
  }

  executeSolution(data: OmitRSEData): Observable<boolean> {
    const result = new ReplaySubject<boolean>(1);

    const computer$ = this.computersFacade.getByHid(data?.additionalInfo?.computerHid);
    const plans$ = this.facade.plansByHid$(data?.additionalInfo?.computerHid);

    combineLatest([computer$, plans$])
      .pipe(
        map(([computer, plans]) => [computer, plans.find((plan) => plan.id === data?.additionalInfo?.planId)]),
        first(),
        switchMap(([computer, plan]: [Computer, PlanInfo]) => {
          if (computer?.hid !== data?.additionalInfo?.computerHid || !plan) return of(null);

          const isNBF = plan?.formatType === PlanFormatTypes.NBF;
          const wizardComponent = getWizardComponentType(plan.type);
          const settings = this.planWrapper.getWizardSettings(plan?.type, computer, plan, true, isNBF);
          settings.firstStepTitle = this.firstStepsFromSolution[data.solution.action_type];

          return this.wizardService.setWizardAndGetRequiredData(wizardComponent, this.modalSettings, settings).afterClosed;
        }),
        untilDestroyed(this)
      )
      .subscribe({
        next: (wizardResult) => result.next(wizardResult === 'Save'),
        error: (e) => result.error(e)
      });

    return result.asObservable();
  }
}
